import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Authentication, AuthData, UserData } from "../../types/types";
import axios, { AxiosResponse } from "axios";
import dayjs from "../../utils/dayjsConfig";
import { deleteRefreshTokenSecure } from "../../utils/biometric";
import { Capacitor } from "@capacitor/core";
import { sessionManager } from "../../utils/sessionManager";
import {
  validateBiometricCredential,
  persistRefreshedBiometricCredential,
  fetchStoredBiometricCredential,
} from "../../utils/biometricSession";
import * as Sentry from "@sentry/react";
import { fetchWithTimeout, withRetry } from "../../utils/withRetry";
import {
  ENABLE_AUTH_LOGS,
  QA_FORCE_AUTH_EXPIRY_MINUTES,
} from "../../constants/logging";
import { queryClient } from "../../queryClient";
import {
  extractDiagnosticHeaders,
  readResponseForLogging,
  redactSensitiveValues,
} from "../../utils/sensitiveDataLogging";
import {
  SENTRY_EVENTS,
  authFlowSlowEvent,
} from "../../constants/sentryEvents";

const authLog = (...args: unknown[]) => {
  if (!ENABLE_AUTH_LOGS) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

type AuthFlowEventOpts = {
  level: "warning" | "error";
  endpoint: string;
  status?: number;
  extra?: Record<string, unknown>;
};

// Single wrapper for every authFlow.* Sentry event so call sites stay focused
// on diagnostic context. Swallows its own errors — Sentry must never block.
const captureAuthFlowEvent = (
  event: string,
  { level, endpoint, status, extra }: AuthFlowEventOpts,
) => {
  try {
    Sentry.captureMessage(event, {
      level,
      tags: {
        module: "authSlice",
        endpoint,
        platform: Capacitor.getPlatform(),
        ...(status !== undefined && { status: String(status) }),
      },
      extra,
    });
  } catch {
    // Sentry must not block.
  }
};

// Above this, even successful auth-flow requests are captured to Sentry so we
// can correlate "Esto está tardando más de lo esperado" reports with backend
// latency. Threshold sits a few seconds above the UX hint (6s) so we only flag
// genuinely slow responses, not anything that briefly trips the spinner copy.
const SLOW_AUTH_REQUEST_THRESHOLD_MS = 15000;

const captureSlowAuthRequest = (
  endpoint: string,
  durationMs: number,
  { status, ...extra }: { status?: number } & Record<string, unknown>,
) => {
  if (durationMs < SLOW_AUTH_REQUEST_THRESHOLD_MS) return;
  captureAuthFlowEvent(authFlowSlowEvent(endpoint), {
    level: "warning",
    endpoint,
    status,
    extra: {
      durationMs,
      thresholdMs: SLOW_AUTH_REQUEST_THRESHOLD_MS,
      ...(status !== undefined && { status }),
      ...extra,
    },
  });
};

// QA-only: shortens auth.expires_at to `execute_at + N minutes` so resume
// biometric reauth can be exercised without a real server-issued expiry.
// No-op when QA_FORCE_AUTH_EXPIRY_MINUTES is null.
const applyQaExpiryOverride = <T extends Partial<AuthData>>(authData: T): T => {
  if (QA_FORCE_AUTH_EXPIRY_MINUTES == null) return authData;
  const executeAt = authData.execute_at;
  if (!executeAt) return authData;
  const overridden = dayjs
    .utc(executeAt)
    .add(QA_FORCE_AUTH_EXPIRY_MINUTES, "minute")
    .toISOString();

  console.log("[QA-EXPIRY-OVERRIDE] auth.expires_at overridden", {
    minutes: QA_FORCE_AUTH_EXPIRY_MINUTES,
    from: authData.expires_at ?? null,
    to: overridden,
    executeAt,
  });
  return { ...authData, expires_at: overridden };
};

const BASE_URL = import.meta.env.VITE_BASE_URL_AUTH;
const UNIFY_BASE_URL_ = import.meta.env.VITE_UNIFY_BASE_URL;
const INVALID_REFRESH_TOKEN_MESSAGE = "Unknown or invalid refresh token";
// Sentinel prefix used in rejectWithValue when /auth/flow/token responds with
// 401 or 500. The reject handlers detect it (via startsWith) and force a
// logout regardless of the body. Format: `auth_token_force_logout:<status>`.
const AUTH_TOKEN_FORCE_LOGOUT_PREFIX = "auth_token_force_logout";

// Clear announcement modals from session storage
// En tu authSlice, actualiza clearAnnouncementModalsSessions:
const clearAnnouncementModalsSessions = () => {
  if (Capacitor.isNativePlatform()) {
    // In native, clear session keys from localStorage
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("announcement_modal_seen_session_")) {
        localStorage.removeItem(key);
      }
    }
  } else {
    // In web, clear from sessionStorage
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith("announcement_modal_seen_session_")) {
        sessionStorage.removeItem(key);
      }
    }
  }
};

// Handle localStorage saved auth and user data (only hydrate on web to avoid native cold-start restore)
const allowLocalHydration = !Capacitor.isNativePlatform();
const localAuth = allowLocalHydration
  ? localStorage.getItem("allaria-auth")
  : null;
const localUser = allowLocalHydration
  ? localStorage.getItem("allaria-user")
  : null;

const parsedLocalAuth: AuthData | null =
  allowLocalHydration && localAuth ? JSON.parse(localAuth) : null;
const parsedLocalUser: UserData | null =
  allowLocalHydration && localUser ? JSON.parse(localUser) : null;

const initialState: Authentication = {
  auth: parsedLocalAuth ?? null,
  user: (parsedLocalAuth && parsedLocalUser) ?? null,
  allariaMasLinkedAccount: false,
  loading: null,
  error: null,
  biometrics: {
    loggedIn: false,
    error: null,
    optInPage: null,
    inProgress: false,
  },
};

const authClient = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  validateStatus: () => true, // let us inspect non-2xx responses
});

const parseAxiosErrorResponse = (response: AxiosResponse) => {
  const { data } = response;
  const message =
    (data &&
      typeof data === "object" &&
      "message" in data &&
      typeof (data as { message?: unknown }).message === "string" &&
      (data as { message?: string }).message) ||
    (typeof data === "string" ? data : "");

  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(response.headers ?? {})) {
    if (typeof value === "string") headers[key] = value;
  }

  return {
    message,
    data,
    text:
      (typeof data === "string" ? data : "") ||
      (response.request && response.request.responseText) ||
      "",
    headers,
  };
};

type RefreshTokenCallResult =
  | { success: true; data: AuthData }
  | { success: false; errorMessage: string; status?: number };

const performRefreshTokenCall = async ({
  refreshToken,
  state,
}: {
  refreshToken?: string | null;
  state?: string | null;
}): Promise<RefreshTokenCallResult> => {
  const payload = {
    refresh_token: refreshToken,
    ...(state ? { state } : {}),
  };

  const url = "/auth/flow/token";
  const startedAt = Date.now();
  if (ENABLE_AUTH_LOGS) {
    authLog("[AUTH-DEBUG] /auth/flow/token: request", {
      url,
      body: redactSensitiveValues(payload),
    });
  }

  const response = await authClient.post(url, payload);

  const headers = extractDiagnosticHeaders(response);
  const durationMs = Date.now() - startedAt;
  if (ENABLE_AUTH_LOGS) {
    authLog("[AUTH-DEBUG] /auth/flow/token: response", {
      status: response.status,
      durationMs,
      headers,
      body: redactSensitiveValues(response.data),
    });
  }

  if (response.status < 200 || response.status >= 300) {
    const errorDetails = parseAxiosErrorResponse(response);
    // Sentry capture for non-2xx — we get this remotely even with auth logs
    // disabled in prod. INVALID_REFRESH_TOKEN_MESSAGE already triggers the
    // silent_logout path downstream; this one captures EVERY non-2xx
    // independently so we can spot 5xx spikes / new error shapes from the
    // backend without waiting for the side effect.
    captureAuthFlowEvent(SENTRY_EVENTS.TOKEN_REQUEST_NON_2XX, {
      level: response.status >= 500 ? "error" : "warning",
      endpoint: "auth_flow_token",
      status: response.status,
      extra: {
        status: response.status,
        durationMs,
        headers,
        responseBody: redactSensitiveValues(response.data),
        serverMessage: errorDetails.message,
      },
    });
    return {
      success: false,
      errorMessage:
        errorDetails.message || errorDetails.text || "Authentication Error",
      status: response.status,
    };
  }

  const parsedData = response.data as AuthData | undefined;
  const data = parsedData
    ? {
        ...parsedData,
        // Use stored/known state; backend does not return state on refresh
        state: state ?? parsedData?.state ?? null,
      }
    : undefined;

  if (!data?.access_token) {
    return { success: false, errorMessage: "Authentication Error" };
  }

  return { success: true, data };
};

export const refreshToken = createAsyncThunk<
  AuthData,
  void,
  { rejectValue: string }
>("authorization/refreshToken", async (_, thunkAPI) => {
  const { getState, rejectWithValue } = thunkAPI;
  const startedAt = Date.now();
  authLog("[AUTH-DEBUG] refreshToken: start");
  try {
    const rootState = getState() as { authentication: Authentication };

    const authentication = rootState?.authentication;

    if (!authentication) return rejectWithValue("Failed to fetch user profile");

    const refreshTokenValue = authentication?.auth?.refresh_token;
    const authState = authentication?.auth?.state;

    const result = await performRefreshTokenCall({
      refreshToken: refreshTokenValue,
      state: authState,
    });
    authLog("[AUTH-DEBUG] refreshToken: result", {
      success: result.success,
      status: "status" in result ? result.status : undefined,
      durationMs: Date.now() - startedAt,
      errorMessage: !result.success ? result.errorMessage : undefined,
    });

    if (result.success) {
      const data = result.data;
      const biometricsEnabled = authentication?.biometrics?.loggedIn;
      const userEmail = authentication?.user?.email;

      if (
        Capacitor.isNativePlatform() &&
        biometricsEnabled &&
        userEmail &&
        data.refresh_token &&
        data.expires_at &&
        data.execute_at
      ) {
        const serverDiffMs = dayjs()
          .utc()
          .diff(dayjs.utc(data.execute_at), "millisecond");
        const storedCredential = await fetchStoredBiometricCredential(
          "auth-slice:refresh-token",
        );

        try {
          await persistRefreshedBiometricCredential({
            username: userEmail,
            refreshToken: data.refresh_token,
            serverDiffMs,
            executeAt: data.execute_at,
            existingExpiresAt: storedCredential?.refreshExpiresAt,
            state: data.state ?? authState ?? null,
          });
        } catch (persistError) {
          Sentry.withScope((scope) => {
            scope.setTag("module", "authSlice");
            scope.setTag("issue", "keychain_persist_failed");
            scope.setTag("platform", Capacitor.getPlatform());
            scope.setExtra(
              "error",
              persistError instanceof Error
                ? persistError.message
                : String(persistError),
            );
            scope.setExtra("userEmail", userEmail);
            scope.setExtra("currentTime", new Date().toISOString());
            Sentry.captureMessage(
              "authSlice.keychain_persist_failed_on_refresh",
              "error",
            );
          });
        }
      } else if (Capacitor.isNativePlatform() && biometricsEnabled) {
        // Biometrics enabled but missing data to persist — keychain will desync from Redux
        Sentry.withScope((scope) => {
          scope.setTag("module", "authSlice");
          scope.setTag("issue", "keychain_persist_skipped");
          scope.setTag("platform", Capacitor.getPlatform());
          scope.setExtra("hasUserEmail", Boolean(userEmail));
          scope.setExtra("hasRefreshToken", Boolean(data.refresh_token));
          scope.setExtra("hasExpiresAt", Boolean(data.expires_at));
          scope.setExtra("hasExecuteAt", Boolean(data.execute_at));
          scope.setExtra("currentTime", new Date().toISOString());
          Sentry.captureMessage(
            "authSlice.keychain_persist_skipped_on_refresh",
            "warning",
          );
        });
      }

      return data;
    } else {
      if (result.status === 401 || result.status === 500) {
        return rejectWithValue(
          `${AUTH_TOKEN_FORCE_LOGOUT_PREFIX}:${result.status}:${result.errorMessage}`,
        );
      }
      return rejectWithValue(result.errorMessage);
    }
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Authentication Error");
  }
});

// Refresh using a provided refresh_token (from secure storage)
export const refreshTokenWithValue = createAsyncThunk<
  AuthData,
  { refreshToken: string; state?: string | null },
  { rejectValue: string }
>(
  "authorization/refreshTokenWithValue",
  async ({ refreshToken: providedRefreshToken, state }, thunkAPI) => {
    const { getState, rejectWithValue } = thunkAPI;
    const rootState = getState() as { authentication: Authentication };
    const authState = rootState?.authentication?.auth?.state;
    const stateToSend = typeof state === "undefined" ? authState : state;

    try {
      const result = await performRefreshTokenCall({
        refreshToken: providedRefreshToken,
        state: stateToSend,
      });

      if (result.success) {
        const data = result.data;
        return data;
      } else {
        if (result.status === 401 || result.status === 500) {
          return rejectWithValue(
            `${AUTH_TOKEN_FORCE_LOGOUT_PREFIX}:${result.status}:${result.errorMessage}`,
          );
        }
        return rejectWithValue(
          result.errorMessage ||
            "Authentication Error or refresh token failed to save securely",
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Authentication Error");
    }
  },
);

// Hydrate biometrics.loggedIn based on secure storage
export const hydrateBiometricsLoggedIn = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>("authorization/hydrateBiometricsLoggedIn", async (_, thunkAPI) => {
  try {
    const storedCredential = await fetchStoredBiometricCredential(
      "auth-slice:hydrate-biometrics",
    );
    const validation = validateBiometricCredential(storedCredential);
    return validation.valid;
  } catch (error) {
    if (error instanceof Error) {
      return thunkAPI.rejectWithValue(error.message);
    }
    return thunkAPI.rejectWithValue("Failed to hydrate biometrics");
  }
});

const CODE_EXCHANGE_TIMEOUT_MS = 20_000;
const USER_REQUEST_TIMEOUT_MS = 10_000;

// Sentinel returned via rejectWithValue when the backend responds with 4xx
// for the post-auth requests. Semantically means "this user doesn't have
// access" (Apple reviewer with SiwA but no Allaria account, account without
// OPERATOR role, etc.). LoginPage catches it and routes to /error-role —
// preserving the existing "Acceso limitado" UX. NOT a network failure.
export const AUTH_NO_ACCESS_REJECTION = "no_access";

export const codeExchange = createAsyncThunk<
  AuthData,
  { code: string; state: string | null },
  { rejectValue: string }
>("authorization/codeExchange", async ({ code, state }, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  const startedAt = Date.now();
  authLog("[AUTH-DEBUG] codeExchange: start", {
    codeLength: code.length,
    hasState: Boolean(state),
    url: `${BASE_URL}/auth/flow/code-exchange`,
  });
  Sentry.addBreadcrumb({
    category: "auth.flow",
    message: "code-exchange request start",
    level: "info",
  });
  try {
    const response = await fetchWithTimeout(
      `${BASE_URL}/auth/flow/code-exchange?authorization_code=${code}${state ? `&state=${state}` : ""}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        }, // Prevent Capacitor native bridge from defaulting to text/plain on Android
        timeoutMs: CODE_EXCHANGE_TIMEOUT_MS,
      },
    );
    const { text, logText, getRedactedParsed } =
      await readResponseForLogging(response);
    let data: AuthData;
    try {
      data = JSON.parse(text) as AuthData;
    } catch {
      data = null as unknown as AuthData;
    }
    const durationMs = Date.now() - startedAt;
    const headers = extractDiagnosticHeaders(response);
    if (ENABLE_AUTH_LOGS) {
      authLog("[AUTH-DEBUG] codeExchange: response", {
        status: response.status,
        durationMs,
        headers,
        body: getRedactedParsed() ?? logText,
      });
    }
    Sentry.addBreadcrumb({
      category: "auth.flow",
      message: "code-exchange request done",
      data: { status: response.status, durationMs },
      level: response.status >= 400 ? "warning" : "info",
    });
    captureSlowAuthRequest("code_exchange", durationMs, {
      status: response.status,
    });

    // Non-2xx: capture rich diagnostic context so we can debug remotely
    // when a specific user can't login. LoginPage's downstream catch fires
    // a separate, less detailed Sentry event; this one carries the full
    // (redacted) response body and headers.
    if (response.status < 200 || response.status >= 300) {
      captureAuthFlowEvent(SENTRY_EVENTS.CODE_EXCHANGE_NON_2XX, {
        level: response.status >= 500 ? "error" : "warning",
        endpoint: "auth_flow_code_exchange",
        status: response.status,
        extra: {
          status: response.status,
          durationMs,
          headers,
          responseBody: getRedactedParsed() ?? logText,
        },
      });
      return rejectWithValue(`Code exchange failed: ${response.status}`);
    }

    if (data?.access_token) {
      return {
        ...data,
        state: state ?? data?.state ?? null,
      };
    }

    captureAuthFlowEvent(SENTRY_EVENTS.CODE_EXCHANGE_INVALID_BODY, {
      level: "error",
      endpoint: "auth_flow_code_exchange",
      extra: {
        status: response.status,
        durationMs,
        headers,
        responseBody: getRedactedParsed() ?? logText,
      },
    });
    return rejectWithValue(
      "Failed to exchange code for token or refresh token failed to save securely",
    );
  } catch (error) {
    const durationMs = Date.now() - startedAt;
    authLog("[AUTH-DEBUG] codeExchange: threw", {
      durationMs,
      message: (error as Error)?.message,
      name: (error as Error)?.name,
    });
    Sentry.addBreadcrumb({
      category: "auth.flow",
      message: "code-exchange request failed",
      data: {
        durationMs,
        message: (error as Error)?.message,
        name: (error as Error)?.name,
      },
      level: "error",
    });
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Failed to exchange code for token");
  }
});

export const getUserProfile = createAsyncThunk<
  UserData,
  void,
  { rejectValue: string }
>("authorization/getUserProfile", async (_, { getState, rejectWithValue }) => {
  const startedAt = Date.now();
  authLog("[AUTH-DEBUG] getUserProfile: start");
  Sentry.addBreadcrumb({
    category: "auth.flow",
    message: "getUserProfile request start",
    level: "info",
  });
  try {
    const state = getState() as { authentication: Authentication };

    const authentication = state?.authentication;

    if (!authentication) return rejectWithValue("Failed to fetch user profile");

    const token = authentication?.auth?.access_token;

    const response = await withRetry(
      async () => {
        const r = await fetchWithTimeout(`${BASE_URL}/user`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeoutMs: USER_REQUEST_TIMEOUT_MS,
        });
        // 5xx is a real backend error → throw so withRetry retries.
        // 2xx/4xx are valid responses we want to inspect at thunk level.
        if (r.status >= 500) {
          throw new Error(`Server error: ${r.status}`);
        }
        return r;
      },
      { maxAttempts: 3, baseDelayMs: 1000, context: "getUserProfile" },
    );

    const {
      text: userText,
      logText: userLogText,
      getRedactedParsed: getRedactedUserBody,
    } = await readResponseForLogging(response);
    const durationMs = Date.now() - startedAt;
    const headers = extractDiagnosticHeaders(response);
    const userEmailHint = authentication?.user?.email ?? null;
    if (ENABLE_AUTH_LOGS) {
      authLog("[AUTH-DEBUG] getUserProfile: response", {
        status: response.status,
        durationMs,
        headers,
        body: getRedactedUserBody() ?? userLogText,
      });
    }
    Sentry.addBreadcrumb({
      category: "auth.flow",
      message: "getUserProfile request done",
      data: { status: response.status, durationMs },
      level: response.status >= 400 ? "warning" : "info",
    });
    captureSlowAuthRequest("user_profile", durationMs, {
      status: response.status,
      userEmailHint,
    });

    // 4xx — backend says no profile / no access for this user. Reject with
    // sentinel so LoginPage routes to /error-role (existing "Acceso limitado"
    // UX) instead of the inline retry UI (which is for network failures).
    if (response.status >= 400) {
      captureAuthFlowEvent(SENTRY_EVENTS.USER_PROFILE_4XX, {
        level: "warning",
        endpoint: "user",
        status: response.status,
        extra: {
          status: response.status,
          durationMs,
          headers,
          responseBody: getRedactedUserBody() ?? userLogText,
          userEmailHint,
        },
      });
      return rejectWithValue(AUTH_NO_ACCESS_REJECTION);
    }

    let user: UserData | null;
    try {
      user = JSON.parse(userText) as UserData;
    } catch {
      user = null;
    }

    if (user) return user;

    captureAuthFlowEvent(SENTRY_EVENTS.USER_PROFILE_INVALID_BODY, {
      level: "error",
      endpoint: "user",
      extra: {
        status: response.status,
        durationMs,
        headers,
        responseBody: getRedactedUserBody() ?? userLogText,
      },
    });
    return rejectWithValue("Failed to fetch user profile");
  } catch (error) {
    authLog("[AUTH-DEBUG] getUserProfile: threw", {
      durationMs: Date.now() - startedAt,
      message: (error as Error)?.message,
      name: (error as Error)?.name,
    });
    Sentry.addBreadcrumb({
      category: "auth.flow",
      message: "getUserProfile request failed",
      data: {
        durationMs: Date.now() - startedAt,
        message: (error as Error)?.message,
        name: (error as Error)?.name,
      },
      level: "error",
    });
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Failed to fetch user profile");
  }
});

type UserAccountsResponse = Promise<{
  user_type: "REGULAR" | "COMMERCIAL";
  esco: {
    accounts: {
      description: string;
      id: string;
      is_enabled: boolean;
      is_natural_person: boolean;
      operate_transfers: boolean;
    }[];
  };
}>;

export const getUserAccounts = async () => {
  const localAuth = localStorage.getItem("allaria-auth");

  const parsedLocalAuth: AuthData | null =
    (localAuth && JSON.parse(localAuth)) ?? null;

  const token = parsedLocalAuth?.access_token;

  const response = await axios.get(
    `${BASE_URL}/user?include=esco_accounts&include=esco_user_type`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Encoding": "gzip",
      },
    },
  );

  return response.data as UserAccountsResponse;
};

export const checkAllariaMasLinked = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  "authorization/checkAllariaMasLinked",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { authentication: Authentication };

      const authentication = state?.authentication;

      if (!authentication)
        return rejectWithValue("Failed to fetch user profile");

      const token = authentication?.auth?.access_token;

      const response = await fetch(
        `${UNIFY_BASE_URL_}/go-to/allaria-mas/enabled`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data: { enabled: boolean } = await response.json();

      return data
        ? data?.enabled
        : rejectWithValue("Failed to check Allaria Más linked account");
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to check Allaria Más linked account");
    }
  },
);

interface ValidateRolesResponse {
  hasAuthorization: boolean;
  hasOperatorRole: boolean;
}

export const validateUserRoles = createAsyncThunk<
  ValidateRolesResponse,
  void,
  { rejectValue: string }
>(
  "authorization/validateUserRoles",
  async (_, { getState, rejectWithValue }) => {
    const startedAt = Date.now();
    authLog("[AUTH-DEBUG] validateUserRoles: start");
    Sentry.addBreadcrumb({
      category: "auth.flow",
      message: "validateUserRoles request start",
      level: "info",
    });
    try {
      const state = getState() as { authentication: Authentication };
      const authentication = state?.authentication;

      if (!authentication?.auth?.access_token) {
        return rejectWithValue("No authentication token available");
      }

      const token = authentication.auth.access_token;

      const response = await withRetry(
        () =>
          axios.get(
            `${BASE_URL}/user?include=esco_accounts&include=esco_user_type`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Encoding": "gzip",
              },
              timeout: USER_REQUEST_TIMEOUT_MS,
              // 4xx are valid responses meaning "no access" — don't throw
              // (otherwise withRetry sees them as retryable errors). 5xx
              // still throws and triggers the retry.
              validateStatus: (status) => status < 500,
            },
          ),
        { maxAttempts: 3, baseDelayMs: 1000, context: "validateUserRoles" },
      );

      const axiosHeaders = extractDiagnosticHeaders({
        headers: response.headers as Record<string, unknown>,
      });
      const durationMs = Date.now() - startedAt;
      let cachedRedactedBody: { value: unknown } | null = null;
      const getRedactedBody = () => {
        if (!cachedRedactedBody) {
          cachedRedactedBody = { value: redactSensitiveValues(response.data) };
        }
        return cachedRedactedBody.value;
      };
      if (ENABLE_AUTH_LOGS) {
        authLog("[AUTH-DEBUG] validateUserRoles: response", {
          status: response.status,
          durationMs,
          headers: axiosHeaders,
          body: getRedactedBody(),
        });
      }
      Sentry.addBreadcrumb({
        category: "auth.flow",
        message: "validateUserRoles request done",
        data: { status: response.status, durationMs: Date.now() - startedAt },
        level: response.status >= 400 ? "warning" : "info",
      });
      captureSlowAuthRequest("validate_roles", durationMs, {
        status: response.status,
        userEmailHint: authentication?.user?.email ?? null,
      });

      // 4xx — backend says no access. Resolve gracefully with empty
      // authorization so LoginPage's existing branch (logout + /error-role)
      // fires for the "Acceso limitado" UX. NOT a network failure.
      if (response.status >= 400) {
        captureAuthFlowEvent(SENTRY_EVENTS.VALIDATE_ROLES_4XX, {
          level: "warning",
          endpoint: "user_with_accounts",
          status: response.status,
          extra: {
            status: response.status,
            durationMs,
            headers: axiosHeaders,
            responseBody: getRedactedBody(),
            userEmailHint: authentication?.user?.email ?? null,
          },
        });
        return { hasAuthorization: false, hasOperatorRole: false };
      }

      const data = response.data;

      // Seed the userAccounts React Query cache with this response so
      // useAccountData doesn't refetch the same endpoint right after login.
      queryClient.setQueryData(["userAccounts"], data);

      const isDev = import.meta.env.VITE_ENVIRONMENT == "dev";

      const hasAuthorization = isDev ? true : !!data?.authorization;
      const roles = data?.authorization?.new_allaria_platform?.roles || [];
      const hasOperatorRole = !roles.includes("BLACKLIST");

      // 2xx but missing role — capture once with redacted body so we can
      // diagnose users who report "Acceso limitado" after a legit login.
      if (!hasAuthorization || !hasOperatorRole) {
        captureAuthFlowEvent(SENTRY_EVENTS.VALIDATE_ROLES_NO_ACCESS, {
          level: "warning",
          endpoint: "user_with_accounts",
          extra: {
            hasAuthorization,
            hasOperatorRole,
            roleCount: roles.length,
            durationMs,
            userEmailHint: authentication?.user?.email ?? null,
          },
        });
      }

      return { hasAuthorization, hasOperatorRole };
    } catch (error) {
      authLog("[AUTH-DEBUG] validateUserRoles: threw", {
        durationMs: Date.now() - startedAt,
        message: (error as Error)?.message,
        name: (error as Error)?.name,
      });
      Sentry.addBreadcrumb({
        category: "auth.flow",
        message: "validateUserRoles request failed",
        data: {
          durationMs: Date.now() - startedAt,
          message: (error as Error)?.message,
          name: (error as Error)?.name,
        },
        level: "error",
      });
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to validate user roles");
    }
  },
);

const captureRefreshTokenLogout = (
  trigger: string,
  payload: string,
  state: Authentication,
) => {
  authLog("[AUTH-DEBUG] silent_logout_refresh_rejected", {
    trigger,
    payload,
  });
  Sentry.withScope((scope) => {
    scope.setTag("module", "authSlice");
    scope.setTag("logout_trigger", trigger);
    scope.setTag("platform", Capacitor.getPlatform());
    scope.setTag("isNative", String(Capacitor.isNativePlatform()));
    scope.setExtra("serverMessage", payload);
    scope.setExtra("expiresAt", state.auth?.expires_at ?? null);
    scope.setExtra("executeAt", state.auth?.execute_at ?? null);
    scope.setExtra("currentTime", new Date().toISOString());
    scope.setExtra("userEmail", state.user?.email ?? null);
    Sentry.captureMessage(
      "authSlice.silent_logout_refresh_rejected",
      "warning",
    );
  });
};

const authSlice = createSlice({
  name: "authorization",
  initialState,
  reducers: {
    logout: (state) => {
      state.auth = null;
      state.user = null;
      state.loading = null;
      state.error = null;
      state.biometrics.loggedIn = false;
      state.biometrics.error = null;
      state.biometrics.inProgress = false;

      Sentry.setUser(null);
      Sentry.setContext("account", null);

      sessionManager.clearSession();

      // Clear announcement modals session storage
      clearAnnouncementModalsSessions();

      localStorage.setItem("logout", Date.now().toString());

      // Delete secure refresh token from keychain/keystore
      deleteRefreshTokenSecure();
    },
    removeBiometricData: (state) => {
      deleteRefreshTokenSecure();
      state.biometrics.loggedIn = false;
      state.biometrics.error = null;
      state.biometrics.optInPage = null;
      state.biometrics.inProgress = false;
    },
    biometricsLoggedIn: (state, action) => {
      state.biometrics.loggedIn = action.payload;
    },
    biometricsOptInPage: (state, action) => {
      state.biometrics.optInPage = action.payload;
    },
    updateBiometricError: (state, action) => {
      state.biometrics.error = action.payload;
    },
    setBiometricInProgress: (state, action) => {
      state.biometrics.inProgress = action.payload;
    },
    setAuthFromUrl: (state, action) => {
      const {
        accessToken,
        refreshToken,
        expiresIn,
        expiresAt,
        executeAt,
        state: authStateFromUrl,
      } = action.payload;

      const rawAuthData: AuthData = {
        access_token: accessToken,
        refresh_token: refreshToken || "",
        expires_in: expiresIn ? Number(expiresIn) : 0,
        expires_at: expiresAt || "",
        token: accessToken,
        execute_at: executeAt || "",
        state: authStateFromUrl ?? null,
      };
      const authData = applyQaExpiryOverride(rawAuthData);

      localStorage.setItem("allaria-auth", JSON.stringify(authData));
      state.auth = authData;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        if (payload) {
          const updatedAuth = applyQaExpiryOverride({
            ...state.auth,
            ...payload,
          });
          localStorage.setItem("allaria-auth", JSON.stringify(updatedAuth));
          state.auth = updatedAuth;
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;

        const payload = action.payload;

        const isInvalidRefreshToken =
          !!payload && payload.includes(INVALID_REFRESH_TOKEN_MESSAGE);
        const isForcedLogoutByStatus =
          !!payload && payload.startsWith(AUTH_TOKEN_FORCE_LOGOUT_PREFIX);

        // If the refresh token is invalid/expired, or the server responded
        // with 401/500, log out the user.
        if (payload && (isInvalidRefreshToken || isForcedLogoutByStatus)) {
          captureRefreshTokenLogout(
            isForcedLogoutByStatus
              ? "refresh_token_status_forced_logout"
              : "refresh_token_rejected",
            payload,
            state,
          );

          state.auth = null;
          state.user = null;
          state.loading = null;
          state.error = payload;
          state.biometrics.loggedIn = false;
          state.biometrics.error = null;

          sessionManager.clearSession();

          // Clear announcement modals session storage
          clearAnnouncementModalsSessions();

          localStorage.setItem("logout", Date.now().toString());
          deleteRefreshTokenSecure();
          return;
        }

        if (payload) {
          state.error = payload;
        }
      })
      .addCase(codeExchange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(codeExchange.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        if (payload) {
          const overridden = applyQaExpiryOverride(payload);
          localStorage.setItem("allaria-auth", JSON.stringify(overridden));
          state.auth = overridden;
        }
      })
      .addCase(codeExchange.rejected, (state, action) => {
        state.loading = false;
        state.auth = null;
        state.user = null;

        const payload = action.payload;

        if (payload) {
          state.error = payload;
        }
      })
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload;

        if (payload) {
          localStorage.setItem("allaria-user", JSON.stringify(payload));
          state.user = payload;

          Sentry.setUser({
            email: payload.email,
          });
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.user = null;

        const payload = action.payload;

        if (payload) {
          state.error = payload;
        }
      })
      .addCase(checkAllariaMasLinked.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAllariaMasLinked.fulfilled, (state, action) => {
        state.loading = false;
        state.allariaMasLinkedAccount = action.payload;
      })
      .addCase(checkAllariaMasLinked.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ?? "Failed to check Allaria Más linked account";
      })
      .addCase(refreshTokenWithValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshTokenWithValue.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        if (payload) {
          const updatedAuth = applyQaExpiryOverride({
            ...state.auth,
            ...payload,
          });
          localStorage.setItem("allaria-auth", JSON.stringify(updatedAuth));
          state.auth = updatedAuth;
        }
      })
      .addCase(refreshTokenWithValue.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;

        const isInvalidRefreshToken =
          !!payload && payload.includes(INVALID_REFRESH_TOKEN_MESSAGE);
        const isForcedLogoutByStatus =
          !!payload && payload.startsWith(AUTH_TOKEN_FORCE_LOGOUT_PREFIX);

        // If the refresh token is invalid/expired, or the server responded
        // with 401/500, log out the user.
        if (payload && (isInvalidRefreshToken || isForcedLogoutByStatus)) {
          captureRefreshTokenLogout(
            isForcedLogoutByStatus
              ? "refresh_token_with_value_status_forced_logout"
              : "refresh_token_with_value_rejected",
            payload,
            state,
          );

          state.auth = null;
          state.user = null;
          state.loading = null;
          state.error = payload;
          state.biometrics.loggedIn = false;
          state.biometrics.error = null;

          sessionManager.clearSession();

          // Clear announcement modals session storage
          clearAnnouncementModalsSessions();

          localStorage.setItem("logout", Date.now().toString());
          deleteRefreshTokenSecure();

          Sentry.setUser(null);
          return;
        }
        if (payload) state.error = payload;
      })
      .addCase(hydrateBiometricsLoggedIn.fulfilled, (state, action) => {
        state.biometrics.loggedIn = action.payload;
      })
      .addCase(validateUserRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateUserRoles.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(validateUserRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to validate user roles";
      });
  },
});
export const {
  logout,
  biometricsLoggedIn,
  biometricsOptInPage,
  updateBiometricError,
  setBiometricInProgress,
  removeBiometricData,
  setAuthFromUrl,
} = authSlice.actions;
export default authSlice.reducer;
